/**
 * BubblePop - the first game built on the GAME-LEVEL-ENGINE.
 *
 * Bubbles drift up carrying things. The child says what they see and the
 * bubble pops. Phases 1 to 3, nine levels.
 *
 * =============================================================================
 * WHAT THIS FILE IS RESPONSIBLE FOR
 *
 * The mechanic, and nothing else. Content is `content/`, this game's judgements
 * are `logic/`, pixels are `screens/` and `parts/`, and the TRIAL is the
 * platform's - `shared/trialLoop.js`, one implementation, used by every game
 * and by the test harness. This file owns what the child sees, what happens
 * when they get it, and what happens when they do not.
 *
 * =============================================================================
 * ONE SESSION
 *
 *   session   ask the engine where this child starts (section 9.2)
 *   level     a pass is a handful of rounds; a level is REPLAYED until its
 *             boxes have ten plays each
 *   round     a field of bubbles, drawn from the level's pool
 *   step      one bubble to name
 *   trial     one go at it - shared/trialLoop.js
 *
 * Everything the loop touches lives in refs. React state is a projection of it
 * for rendering: a setState that lands one frame late is a nuisance in a UI and
 * a corrupted trial record in a measurement instrument.
 *
 * =============================================================================
 * THE MICROPHONE IS ALWAYS ON
 *
 * Section 2.1: voice is the controller. There is no push-to-talk anywhere in
 * this game; recognition starts when play starts and runs continuously for the
 * whole session. The only taps are for grown-ups.
 *
 * That means Pip's voice goes into a live microphone (audit finding B42, which
 * all four existing games have and none defend against). The defence is in the
 * platform: everything Pip says is stamped as a window, and neither a
 * transcript nor a vocalisation inside one is attributed to the child.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './BubblePop.css';

import { useSpeechRecognition } from '../../../hooks/useSpeechRecognition';
import { useSpeechSynthesis } from '../../../hooks/useSpeechSynthesis';
import { useAudioEffects } from '../../../hooks/useAudioEffects';
import { useAudioEnergy } from '../../../hooks/useAudioEnergy';

import { getLevel, getPromptStyle, getStrictness } from '../../../engine/framework.js';
import { evaluateProgression } from '../../../engine/progression.js';
import { levelBoxSummary } from '../../../engine/contract.js';

import { runTrial } from '../shared/trialLoop.js';
import { createBrowserStage } from '../shared/browserStage.js';
import { createStep } from '../shared/step.js';
import { readRecommendation } from '../shared/pacing.js';
import { placeChildInGame } from '../shared/profile.js';
import { openingRungIndex, rungIndexOf } from '../shared/support.js';
import { createRng } from '../shared/rounds.js';
import { createBags } from '../shared/lineBag.js';
import useAttemptRecorder from '../shared/useAttemptRecorder.js';

import { createAdapter } from './adapter.js';
import { GAME, POOLS } from './content/index.js';
import { LEVEL_INTRO, ROUND_LINKS, LEVEL_COMPLETE, SESSION_END } from './content/voice.js';
import { nextInGame, previousInGame, contentProgress } from './logic/placement.js';

import StartScreen from './screens/StartScreen';
import PlayScreen from './screens/PlayScreen';
import LevelCompleteOverlay from './screens/LevelCompleteOverlay';
import EndScreen from './screens/EndScreen';
import GrownUpsPanel from './parts/GrownUpsPanel';

const SCREEN = { START: 'start', PLAY: 'play', END: 'end' };

/** How long a pop, and its celebration, hold the screen. */
const POP_MS = 700;

/** Auto-advance from the level-complete card, so nobody has to tap. */
const LEVEL_CARD_MS = 7000;

/** The panel's "end this session", and what a jump to nowhere means. */
const END = 'end';

export default function BubblePop({ onExit }) {
  // ==========================================================================
  // HOOKS
  // ==========================================================================
  const speech = useSpeechRecognition();
  const synth = useSpeechSynthesis();
  const [soundOn, setSoundOn] = useState(true);
  const fx = useAudioEffects(soundOn);
  const energy = useAudioEnergy();

  /**
   * Every hook object the loop touches, refreshed on each render. The loop
   * reads the CURRENT hooks through this ref, because a timer scheduled eight
   * seconds ago must not fire into a closure that captured a stale one.
   */
  const env = useRef({});
  env.current = { ...env.current, speech, synth, fx, energy, soundOn };

  // ==========================================================================
  // RENDERED STATE - a projection of the refs below
  // ==========================================================================
  const [screen, setScreen] = useState(SCREEN.START);
  const [child, setChild] = useState(null);
  const [position, setPosition] = useState(null);
  const [placement, setPlacement] = useState(null);
  const [view, setView] = useState({
    scene: 'meadow', bubbles: [], prompt: null, cue: null, chips: null,
    highlight: null, pointing: false, phase: 1, level: 1, rungId: 'full_model',
    round: 0, rounds: 0,
  });
  const [pip, setPip] = useState({ mood: 'happy', animation: 'idle', bubble: '' });
  const [banner, setBanner] = useState(null);
  const [levelCard, setLevelCard] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [recommendation, setRecommendation] = useState(null);
  const [levelState, setLevelState] = useState(null);           // the chart, per level
  const [profilePosition, setProfilePosition] = useState(null); // section 9.1, from the server
  const [stats, setStats] = useState({ trials: 0, popped: 0, vocalised: 0, levels: 0, startedAt: null });

  // ==========================================================================
  // THE SESSION'S OWN STATE
  // ==========================================================================
  const positionRef = useRef(null);          // {phase, level}
  const contentRef = useRef(null);           // content/levels.js entry
  const bubblesRef = useRef([]);

  const supportIndexRef = useRef(0);         // the session's rung for this level
  const supportIncreasesRef = useRef(0);
  const cleanRunRef = useRef(0);             // consecutive unaided successes
  const attemptsAtLevelRef = useRef([]);     // whole-attempt outcomes, oldest first
  const levelScoreRef = useRef(null);        // the chart's verdict, from the server
  const offerRef = useRef(null);             // the engine's advance/step-back offer
  const stepStateRef = useRef(null);         // shared/step.js, for the step in progress

  // When each target phrase was last modelled aloud, across trials. Feeds the
  // record's priorModelAgoMs: Phase 3 D is judged over the trial, and the
  // cross-trial echo that deliberately misses is recorded beside it.
  const modelledAtRef = useRef(new Map());

  const adapterRef = useRef(null);
  const stageRef = useRef(null);
  const bagsRef = useRef(null);
  const rngRef = useRef(null);
  const aliveRef = useRef(true);
  const jumpRef = useRef(null);              // a grown-up took the session somewhere
  const cardChoiceRef = useRef(null);        // resolves the level-complete card

  const capabilities = GAME.capabilities;

  // ==========================================================================
  // PERSISTENCE
  // ==========================================================================
  const onServerRecommendation = useCallback((rec, result) => {
    // The chart's own verdict on this level (section 7.5). The local evaluator
    // cannot compute it - it holds no box histories - so it is remembered here
    // and handed back to the engine on the next trial, which is what lets a
    // finished level be offered onward without waiting for a round trip.
    if (result && result.level && positionRef.current
        && result.level.phase === positionRef.current.phase
        && result.level.level === positionRef.current.level) {
      levelScoreRef.current = result.level;
      setLevelState(result.level);
    }

    // Section 9.1: the child's global position is the engine's to write, and it
    // has just written it. The game keeps a copy only so the grown-ups panel
    // does not show a stale profile - it is never the source of anything.
    if (result && result.position) setProfilePosition(result.position);

    // Computed from the child's whole history, which beats anything this
    // session can see, so it wins over the local one. It arrives late, and that
    // is fine: pacing is a decision about the next level, not the next syllable.
    applyPacing(rec, 'server');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const recorder = useAttemptRecorder({
    childId: child ? child.id : null,
    gameId: GAME.id,
    onRecommendation: onServerRecommendation,
  });
  env.current.recorder = recorder;

  // ==========================================================================
  // THE STAGE - everything the trial loop needs from a real device
  // ==========================================================================
  if (!stageRef.current) {
    stageRef.current = createBrowserStage({
      env,
      post: (payload) => env.current.recorder.record(payload),
      onSay: (text) => setPip((p) => ({ ...p, bubble: text })),
      show: (patch) => {
        if (patch.kind === 'prompt' || patch.kind === 'ladder') {
          const opening = patch.kind === 'prompt';
          setView((v) => ({
            ...v,
            rungId: patch.rungId,
            prompt: patch.prompt.text,
            cue: patch.prompt.cue,
            chips: patch.prompt.chips,
            hint: opening ? null : v.hint,
            // The picture is a cue in its own right at every rung from `visual`
            // downward. At the gestural rung Pip points instead.
            highlight: opening && patch.rungId === 'gestural' ? null : patch.target.objectId,
            pointing: opening && patch.rungId === 'gestural' ? patch.target.objectId : null,
            status: 'prompting',
          }));
          if (opening) setPip((p) => ({ ...p, animation: 'idle', mood: 'curious' }));
          return;
        }
        if (patch.kind === 'hint') {
          setView((v) => ({ ...v, hint: patch.text }));
          return;
        }
        if (patch.kind === 'listening') {
          setView((v) => ({ ...v, status: 'listening' }));
          setPip((p) => ({ ...p, animation: 'idle', mood: 'happy' }));
        }
      },
    });
    // A model is a thing Pip DID, wherever the ladder happened to be. Kept
    // across trials so the next one can say how long ago it was.
    stageRef.current.noteModel = (phrase, at) => modelledAtRef.current.set(phrase, at);
  }

  const say = useCallback((text, opts) => stageRef.current.say(text, opts), []);
  const pause = useCallback(
    (ms) => new Promise((resolve) => { stageRef.current.later(ms, resolve); }),
    []
  );
  /** Has something taken the session out from under the trial in progress? */
  const interrupted = useCallback(() => !aliveRef.current || jumpRef.current !== null, []);

  /** Pick a line from a pool, never the same one twice running. */
  const line = useCallback((pool, prefer) => {
    if (!bagsRef.current) return '';
    const bag = bagsRef.current[pool];
    return bag ? bag.next(prefer) : '';
  }, []);

  const paintBubbles = useCallback(() => {
    setView((v) => ({ ...v, bubbles: bubblesRef.current.map((b) => ({ ...b })) }));
  }, []);

  // ==========================================================================
  // PACING (section 8.4) - the engine's, in both directions, never this game's
  // ==========================================================================
  function applyPacing(rec, source) {
    if (!rec) return;
    setRecommendation({ ...rec, source });

    const phase = positionRef.current ? positionRef.current.phase : 1;
    const read = readRecommendation(rec, { phase, supportIndex: supportIndexRef.current });

    if (read.support && read.support.changed) {
      supportIndexRef.current = read.support.index;
      if (read.support.raise) {
        // Support before failure (section 2.4): a raise takes effect on the very
        // next trial, including a retry of the step in progress. A FADE
        // deliberately does not touch the step in progress - making a trial
        // harder half way through is not what earning independence should feel
        // like, so it takes effect at the next step.
        supportIncreasesRef.current += 1;
        if (stepStateRef.current) stepStateRef.current.supportRaised(read.support.index);
      }
      cleanRunRef.current = 0;
    }

    // An offer is an offer (ENGINE-BUILD.md section 3.6): held until the level
    // ends and presented there, never taken on the child's behalf mid-level.
    if (read.offer) offerRef.current = read.offer;
  }

  // ==========================================================================
  // SUCCESS
  // ==========================================================================
  const succeed = useCallback(async (result, target) => {
    // What the child actually asked for, which at phase 3 need not be what Pip
    // suggested. The world answers the request that was made.
    const detail = result.judged.detail || {};
    const objectId = detail.objectId || target.objectId;
    const effect = detail.effect || 'pop';

    const bubble = bubblesRef.current.find((b) => b.objectId === objectId && b.state === 'idle');
    if (bubble) {
      bubble.state = effect === 'give' ? 'given' : effect === 'place' ? 'placed' : 'popping';
      paintBubbles();
    }
    env.current.fx.playPop?.();
    stageRef.current.later(120, () => env.current.fx.playSuccess?.());

    // Consecutive unaided successes. The ENGINE decides what this earns; the
    // game keeps the count only to choose a tone - a child who is flying gets a
    // brighter line than one who has just needed three prompts.
    cleanRunRef.current = result.unaided ? cleanRunRef.current + 1 : 0;

    setStats((s) => ({ ...s, popped: s.popped + 1 }));
    setPip((p) => ({ ...p, animation: 'celebrate', mood: 'excited' }));

    const praise = line('praise', cleanRunRef.current >= 1 ? 'bright' : 'calm');
    setBanner({ kind: 'praise', text: praise });
    await say(praise, { praise: true });

    await pause(POP_MS);
    if (bubble) bubble.state = 'gone';
    paintBubbles();
    setBanner(null);
    setPip((p) => ({ ...p, animation: 'idle', mood: 'happy' }));
  }, [line, pause, say, paintBubbles]);

  // ==========================================================================
  // NOT YET - and never a failure
  // ==========================================================================
  const tryAgain = useCallback(async (result) => {
    // Three different things just happened and they deserve three different
    // answers. Only the last is anything like an error.
    const madeASound = result.qualifying > 0;
    const saidSomething = Boolean(result.judged.heard);
    const partial = Boolean(result.judged.detail && result.judged.detail.partial);

    const pool = partial || (saidSomething && result.heardWell.reliable) ? 'nearly' : 'encouragement';
    const text = line(pool, 'calm');
    setBanner({ kind: madeASound ? 'heard' : 'listening', text });
    setPip((p) => ({ ...p, mood: 'curious', animation: 'idle' }));
    await say(text);
  }, [line, say]);

  /**
   * Pip does it with them.
   *
   * Not recorded as an attempt: nothing was measured, and writing a failure
   * here would punish the child for the game running out of patience. The
   * attempts that WERE made are already in the chart.
   */
  const assist = useCallback(async (target) => {
    const phrase = adapterRef.current.phraseFor(target);
    await say(line('assist'));
    await say(phrase);
    modelledAtRef.current.set(phrase, stageRef.current.now());

    const bubble = bubblesRef.current.find((b) => b.objectId === target.objectId && b.state === 'idle');
    if (bubble) { bubble.state = 'popping'; paintBubbles(); }
    env.current.fx.playPop?.();
    setPip((p) => ({ ...p, animation: 'celebrate', mood: 'happy' }));

    await pause(POP_MS);
    if (bubble) bubble.state = 'gone';
    paintBubbles();
    setPip((p) => ({ ...p, animation: 'idle' }));
  }, [line, pause, say, paintBubbles]);

  // ==========================================================================
  // ONE STEP - one bubble to name, and its trials
  // ==========================================================================
  const playStep = useCallback(async (target, stepIndex) => {
    const { phase, level } = positionRef.current;
    const content = contentRef.current;
    const step = createStep({ phase, openingIndex: supportIndexRef.current });
    stepStateRef.current = step;

    for (;;) {
      // What only this game can know: which bubbles are still on screen, and
      // what the field looks like, for a re-scoring years from now.
      const live = bubblesRef.current.filter((b) => b.state === 'idle');
      const phrase = adapterRef.current.phraseFor(target);
      const lastModelledAt = modelledAtRef.current.get(phrase);

      // eslint-disable-next-line no-await-in-loop
      const result = await runTrial({
        stage: stageRef.current,
        game: adapterRef.current,
        phase,
        level,
        content,
        target: {
          ...target,
          onScreen: live.map((b) => b.objectId),
          gridState: bubblesRef.current.map((b) => ({
            objectId: b.objectId, slot: b.slot, state: b.state,
          })),
        },
        pacing: adapterRef.current.pacing(phase, level),
        openingIndex: step.open(),
        attemptNumber: step.attemptNumber(),
        step: stepIndex + 1,
        supportIncreases: supportIncreasesRef.current,
        priorModelAgoMs: lastModelledAt === undefined
          ? null
          : stageRef.current.now() - lastModelledAt,
      });
      if (result.cancelled || interrupted()) return false;
      if (!result.ok) {
        // The game built something the engine will not accept. Nothing is
        // posted - a bad record is worse than no record - and the child plays on.
        // eslint-disable-next-line no-console
        console.error('[bubble-pop] the attempt was not posted:',
          result.validated.errors, result.check.errors, result.diagnostics);
      }

      setView((v) => ({ ...v, status: 'resolving' }));
      setStats((s) => ({
        ...s,
        trials: s.trials + 1,
        vocalised: s.vocalised + (result.qualifying > 0 ? 1 : 0),
      }));

      // Whole-attempt outcomes at THIS level: "did the child complete the step",
      // which is a different thing from a box, and is what section 8.4 reads.
      attemptsAtLevelRef.current = [...attemptsAtLevelRef.current, result.outcome];

      // The same call the server makes, made locally so the next trial does not
      // have to wait for a round trip. The server's answer wins when it arrives.
      applyPacing(evaluateProgression({
        phase,
        level,
        attempts: attemptsAtLevelRef.current,
        supportIndex: supportIndexRef.current,
        supportIncreases: supportIncreasesRef.current,
        targetSupportIndex: rungIndexOf(content.nominalRung),
        // A level that is Green is finished, whatever the last five attempts
        // looked like (section 7.5). Without it the local evaluator can only
        // ever see the sliding window.
        levelScore: levelScoreRef.current,
        availablePhases: GAME.phasesAvailable,
        availableLevels: GAME.levelsAvailable,
      }), 'local');

      if (result.judged.success) {
        // eslint-disable-next-line no-await-in-loop
        await succeed(result, target);
        return !interrupted();
      }

      const more = step.missed(result);
      // eslint-disable-next-line no-await-in-loop
      await tryAgain(result);
      if (interrupted()) return false;

      if (!more) {
        // eslint-disable-next-line no-await-in-loop
        await assist(target);
        return !interrupted();
      }
      // eslint-disable-next-line no-await-in-loop
      await pause(400);
      if (interrupted()) return false;
    }
  }, [assist, interrupted, pause, succeed, tryAgain]); // eslint-disable-line react-hooks/exhaustive-deps

  // ==========================================================================
  // END OF A LEVEL
  // ==========================================================================
  const chooseFromCard = useCallback((choice) => {
    const resolve = cardChoiceRef.current;
    cardChoiceRef.current = null;
    if (resolve) resolve(choice);
  }, []);

  const completeLevel = useCallback(async () => {
    const { phase, level } = positionRef.current;
    env.current.fx.playFanfare?.();
    setPip((p) => ({ ...p, animation: 'celebrate', mood: 'excited' }));
    setStats((s) => ({ ...s, levels: s.levels + 1 }));

    const offer = offerRef.current;
    // Mastered with nowhere to go: the end of this game's content, and the one
    // signal that says so (GAME-02-CONTRACT.md section 1.4).
    if (offer && offer.kind === 'complete') return null;

    // The engine offers; this game resolves that offer to content it has.
    const nextLevel = offer && offer.kind === 'advance'
      ? nextInGame(phase, level)
      : offer && offer.kind === 'back'
        ? previousInGame(phase, level)
        : null;

    setLevelCard({
      phase,
      level,
      offer: offer ? { ...offer, resolved: nextLevel } : null,
      progress: contentProgress(phase, level),
    });

    await say(line('levelComplete'));
    if (interrupted()) return null;

    // Nobody has to tap. With an offer standing, take it; otherwise play the
    // level again - ten plays is what a box needs before it can be coloured,
    // and a level that ends the session after one pass never colours anything.
    const choice = await new Promise((resolve) => {
      cardChoiceRef.current = resolve;
      stageRef.current.later(LEVEL_CARD_MS, () => {
        if (cardChoiceRef.current === resolve) chooseFromCard('auto');
      });
    });
    setLevelCard(null);
    if (interrupted()) return null;

    if (choice === END) return null;
    if (choice === 'again') return { phase, level };
    if (choice === 'auto') return nextLevel || { phase, level };
    return nextInGame(phase, level);
  }, [chooseFromCard, interrupted, line, say]);

  // ==========================================================================
  // ONE LEVEL
  // ==========================================================================
  const playLevel = useCallback(async (phase, level, { intro = false } = {}) => {
    const content = adapterRef.current.content(phase, level);
    if (!content) return null;

    // A level is REPLAYED until its boxes have ten plays each, so most of what
    // follows belongs to the LEVEL and not to one pass through it. Clearing it
    // at every pass was the first playtest's bug in two ways at once: section
    // 8.4's advance window is five attempts and a pass is three, so the local
    // evaluator could never see a full window; and an offer the engine had
    // already made was thrown away before it could be taken.
    const previous = positionRef.current;
    const sameLevel = Boolean(previous && previous.phase === phase && previous.level === level);

    positionRef.current = { phase, level };
    contentRef.current = content;
    const rounds = adapterRef.current.plan(phase, level);

    if (!sameLevel) {
      attemptsAtLevelRef.current = [];
      supportIncreasesRef.current = 0;
      cleanRunRef.current = 0;
      offerRef.current = null;
      levelScoreRef.current = null;
      setLevelState(null);
    }

    supportIndexRef.current = openingRungIndex({
      phase,
      levelNominalRung: content.nominalRung,
      placementIndex: supportIndexRef.current,
    });

    setPosition({ phase, level });
    setLevelCard(null);
    setView((v) => ({
      ...v, scene: content.scene, phase, level, rounds: content.rounds, round: 0,
    }));

    if (intro) {
      setView((v) => ({ ...v, status: 'prompting' }));
      await say(line(`intro${phase}`));
      if (interrupted()) return null;
    }

    let stepIndex = 0;
    for (let r = 0; r < rounds.length; r += 1) {
      bubblesRef.current = rounds[r].field.map((b) => ({ ...b, state: 'idle' }));
      setView((v) => ({ ...v, round: r, rounds: content.rounds }));
      paintBubbles();
      env.current.fx.playWhoosh?.();

      for (const target of rounds[r].targets) {
        // eslint-disable-next-line no-await-in-loop
        const carriedOn = await playStep(target, stepIndex);
        stepIndex += 1;
        if (!carriedOn) return null;
      }

      if (r < rounds.length - 1) {
        setView((v) => ({ ...v, status: 'prompting' }));
        // eslint-disable-next-line no-await-in-loop
        await say(line('roundLink'));
        if (interrupted()) return null;
      }
    }

    return completeLevel();
  }, [completeLevel, interrupted, line, paintBubbles, playStep, say]);

  // ==========================================================================
  // A SESSION
  // ==========================================================================
  const finishSession = useCallback(async () => {
    env.current.speech.stopListening?.();
    env.current.energy.stop?.();
    env.current.recorder.flush();
    setScreen(SCREEN.END);
    await say(line('sessionEnd'));
  }, [line, say]);

  const beginSession = useCallback(async (chosenChild) => {
    const seed = Math.floor(Math.random() * 2 ** 31) || 1;
    rngRef.current = createRng(seed);
    adapterRef.current = createAdapter({ rng: rngRef.current });
    // Every pool, built up front. A bag created lazily in an effect is not
    // there when the first line is needed, and the first line of a session is
    // the one that greets the child.
    bagsRef.current = createBags({
      ...POOLS,
      roundLink: ROUND_LINKS,
      levelComplete: LEVEL_COMPLETE,
      sessionEnd: SESSION_END,
      intro1: LEVEL_INTRO[1],
      intro2: LEVEL_INTRO[2],
      intro3: LEVEL_INTRO[3],
    }, { rng: rngRef.current });

    setChild(chosenChild);
    setStats({ trials: 0, popped: 0, vocalised: 0, levels: 0, startedAt: Date.now(), seed });

    // Section 9.2: ask the engine where to start. Never decide it here. The
    // phase and level come from the CHILD profile, not from this game - the
    // chart tracks the child (section 9.1).
    const place = placeChildInGame(chosenChild, GAME);
    setPlacement(place);
    supportIndexRef.current = place.supportIndex;

    // The microphone: both paths, on one user gesture. Energy monitoring is
    // what makes Phase 1 component B measurable at all; if it is refused the
    // game still runs and every trial records `monitored: false`, which the
    // engine reads as "not measured" rather than "made no sound".
    await env.current.energy.start();
    env.current.speech.startListening();
    setScreen(SCREEN.PLAY);

    let next = { phase: place.phase, level: place.level };
    let intro = true;
    while (next && aliveRef.current) {
      const from = next;
      // eslint-disable-next-line no-await-in-loop
      next = await playLevel(from.phase, from.level, { intro });

      // A grown-up took the session somewhere while a trial was in flight.
      const jump = jumpRef.current;
      jumpRef.current = null;
      if (jump) next = jump === END ? null : jump;

      intro = Boolean(next) && (next.phase !== from.phase || next.level !== from.level);
    }

    if (aliveRef.current) await finishSession();
  }, [finishSession, playLevel]);

  /** The grown-ups panel, reaching into a session already in progress. */
  const jumpTo = useCallback((target) => {
    jumpRef.current = target;
    chooseFromCard(END);
    stageRef.current.abort();
  }, [chooseFromCard]);

  // ==========================================================================
  // LIFECYCLE
  // ==========================================================================
  useEffect(() => {
    aliveRef.current = true;
    const detach = stageRef.current.attach();
    return () => {
      aliveRef.current = false;
      detach();
      env.current.synth.cancel?.();
      env.current.speech.stopListening?.();
      env.current.energy.stop?.();
    };
  }, []);

  // ==========================================================================
  // RENDER
  // ==========================================================================
  const engineFacts = useMemo(() => {
    if (!position) return null;
    const { phase, level } = position;
    return {
      level: getLevel(phase, level),
      prompt: getPromptStyle(phase),
      strictness: getStrictness(phase),
      boxes: levelBoxSummary(phase, level, capabilities),
    };
  }, [position, capabilities]);

  if (!speech.isSupported) {
    return (
      <div className="bp bp--message">
        <div className="bp__card">
          <div className="bp__card-icon" aria-hidden="true">🎤</div>
          <h2>Bubble Pop needs a microphone</h2>
          <p>
            This game is played with your voice, and this browser does not offer
            speech recognition. Chrome, Edge or Safari will work.
          </p>
          <button type="button" className="bp__btn bp__btn--primary" onClick={onExit}>Go back</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`bp bp--scene-${view.scene}`} data-screen={screen}>
      {screen === SCREEN.START && (
        <StartScreen
          game={GAME}
          onStart={beginSession}
          onExit={onExit}
          speechError={speech.error}
          synthSupported={synth.isSupported}
        />
      )}

      {screen === SCREEN.PLAY && (
        <>
          <PlayScreen
            view={view}
            pip={pip}
            banner={banner}
            listening={{
              isListening: speech.isListening,
              isVocalising: energy.isVocalising,
              rms: energy.rms,
              noiseFloor: energy.noiseFloor,
              monitored: energy.isMonitoring,
            }}
            interim={speech.interimTranscript}
            onOpenPanel={() => setPanelOpen(true)}
          />

          {levelCard && (
            <LevelCompleteOverlay
              card={levelCard}
              onChoose={chooseFromCard}
            />
          )}
        </>
      )}

      {screen === SCREEN.END && (
        <EndScreen
          stats={stats}
          child={child}
          queue={recorder.status}
          onReplay={() => { setScreen(SCREEN.START); }}
          onExit={onExit}
        />
      )}

      {panelOpen && (
        <GrownUpsPanel
          game={GAME}
          child={child}
          placement={placement}
          position={position}
          engineFacts={engineFacts}
          supportIndex={supportIndexRef.current}
          recommendation={recommendation}
          levelState={levelState}
          profilePosition={profilePosition}
          queue={recorder.status}
          rejections={recorder.rejections()}
          stats={stats}
          soundOn={soundOn}
          mic={{
            listening: speech.isListening,
            error: speech.error,
            monitored: energy.isMonitoring,
            permission: energy.permission,
            noiseFloor: energy.noiseFloor,
          }}
          onToggleSound={() => setSoundOn((s) => !s)}
          onClose={() => setPanelOpen(false)}
          onEndSession={() => { setPanelOpen(false); jumpTo(END); }}
          onNextLevel={() => {
            setPanelOpen(false);
            const here = positionRef.current;
            jumpTo((here && nextInGame(here.phase, here.level)) || END);
          }}
        />
      )}
    </div>
  );
}
